<template>
	<div>
		<div class="flex mb-4">
			<p>
				{{ $t("Color") }}:
				{{ selectedColorLocal?.Name || selectedColorLocal?.Code }}
			</p>
		</div>
		<RadioGroup
			:id="`${colors?.[0]?.ParametricCode}`"
			v-model="selectedColorLocal"
		>
			<div class="flex flex-wrap items-center lg:gap-4 gap-2">
				<RadioGroupOption
					v-for="color in colors"
					:id="`${color.ParametricCode}-${color.ParametricId}-${color.Id}`"
					:key="color.Id"
					v-slot="{ checked }"
					as="template"
					:value="color"
				>
					<div
						:class="[
							checked && !shouldShowImage(color)
								? 'ring-[0.5px] ring-primary'
								: '',
							'cursor-pointer',
							'relative flex  items-center justify-center p-0.5 focus:outline-none focus:ring-[0.5px]/focus:ring-offset-2/focus:ring-offset-white focus:ring-gray-super-dark focus:ring-2/focus:ring-offset-2/focus:ring-offset-white '
						]"
					>
						<RadioGroupLabel
							:id="`${color.ParametricCode}-${color.Id}`"
							as="span"
							class="sr-only"
						>
							{{ color.Name || color.Code }}
						</RadioGroupLabel>
						<div
							v-if="shouldShowImage(color)"
							class="p-1 w-20 grid grid-cols-1 gap-2 text-center border-2 relative after:content-[''] after:absolute before:content-[''] before:absolute"
							:class="[
								checked ? 'border-gray-super-dark' : 'border-quaternary'
							]"
						>
							<NuxtImg
								:src="getColorImageUrl(color)"
								:alt="color.Name || color.Code"
								loading="lazy"
								class="w-full h-auto aspect-square object-cover"
							/>
							<span>
								{{ color.Name || color.Code }}
							</span>
						</div>
						<template v-else>
							<span
								aria-hidden="true"
								:class="['h-7 w-7 border-[0.5px] border-gray-darker']"
								:style="{
									backgroundColor: `${color.Code ?? color.Description}`
								}"
							/>
						</template>
						<span
							v-if="!color?.InStock"
							aria-hidden="true"
							class="pointer-events-none left-0 top-0 right-0 bottom-0 absolute border-[0.5px]"
						>
							<svg
								class="absolute inset-0 p-0.5 h-full w-full stroke-[0.5px]"
								viewBox="0 0 100 100"
								preserveAspectRatio="none"
								stroke="currentColor"
							>
								<line
									x1="0"
									y1="100"
									x2="100"
									y2="0"
									vector-effect="non-scaling-stroke"
								/>
							</svg>
						</span>
					</div>
				</RadioGroupOption>
			</div>
		</RadioGroup>
	</div>
</template>

<script setup lang="ts">
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import type {
	ParametricListValue,
	ParametricListValuewithCount
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { watch, watchEffect } from "vue";
import { useColorImage } from "~/layers/base/composables/use-color-image";

const { selectedColor } = storeToRefs(useProductStore());
const { shouldShowImage, getColorImageUrl } = useColorImage();

interface Props {
	parametricsColor:
		| (ParametricListValue | ParametricListValuewithCount)[]
		| undefined;
}
const { parametricsColor } = defineProps<Props>();

const colors = ref<
	(ParametricListValue | ParametricListValuewithCount)[] | undefined
>();
const selectedColorLocal = ref<
	ParametricListValue | ParametricListValuewithCount | undefined
>(selectedColor.value ?? undefined);

watchEffect(() => {
	colors.value = parametricsColor;
});

watch(
	selectedColor,
	(newVal) => {
		if (newVal && newVal.Id !== selectedColorLocal.value?.Id) {
			selectedColorLocal.value = newVal;
		}
	},
	{ immediate: true }
);

watch(selectedColorLocal, () => {
	if (selectedColorLocal.value) {
		useProductStore().addSelectedColor(selectedColorLocal.value);
	}
});
</script>
